import { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, Link } from 'react-router-dom';
import Logo from '../components/Logo';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

function Register() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [legalName, setLegalName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleRegister = async (e) => {
        e.preventDefault();
        setError('');

        if (legalName.trim().length < 2) {
            return setError('Please enter your full legal name.');
        }
        if (password.length < 8) {
            return setError('Password must be at least 8 characters.');
        }
        if (password !== confirm) {
            return setError('Passwords do not match.');
        }

        setLoading(true);
        try {
            const res = await api.post('/auth/register', {
                email: email.trim(),
                password,
                legal_name: legalName.trim(),
            });
            const { accessToken, refreshToken, user } = res.data;
            login(accessToken, refreshToken, user, { authMethod: 'password' });
            navigate('/dashboard');
        } catch (err) {
            setError(err.response?.data?.error || 'Registration failed. Please try again.');
        }
        setLoading(false);
    };

    const handleGoogleSuccess = async (credentialResponse) => {
        setError('');
        setLoading(true);
        try {
            const decoded = jwtDecode(credentialResponse.credential);
            const res = await api.post('/auth/login', {
                email: decoded.email,
                name: decoded.name,
                google_sub: decoded.sub,
                picture: decoded.picture,
            });
            const { accessToken, refreshToken, user } = res.data;
            login(accessToken, refreshToken, user, { authMethod: 'google', picture: decoded.picture });
            navigate('/dashboard');
        } catch (err) {
            setError(err.response?.data?.error || 'Google sign-up failed. Please try again.');
        }
        setLoading(false);
    };

    const inputStyle = {
        width: '100%',
        padding: '14px 16px',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        background: 'rgba(255, 255, 255, 0.05)',
        color: '#ffffff',
        fontSize: '0.95rem',
        boxSizing: 'border-box',
        outline: 'none',
        transition: 'border-color 0.2s, background 0.2s',
    };

    const passwordOk = password.length === 0 || password.length >= 8;
    const confirmOk = confirm.length === 0 || confirm === password;

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            background: 'linear-gradient(180deg, #12121a 0%, #1a1a2e 50%, #12121a 100%)',
            color: '#ffffff',
            padding: '20px',
            position: 'relative',
            overflow: 'hidden',
        }}>
            {/* Gradient orbs for visual interest */}
            <div style={{
                position: 'fixed',
                top: '-20%',
                right: '-10%',
                width: '500px',
                height: '500px',
                background: 'radial-gradient(circle, rgba(255, 193, 7, 0.12) 0%, transparent 70%)',
                borderRadius: '50%',
                pointerEvents: 'none',
                zIndex: 0,
            }} />
            <div style={{
                position: 'fixed',
                bottom: '-30%',
                left: '-10%',
                width: '400px',
                height: '400px',
                background: 'radial-gradient(circle, rgba(255, 193, 7, 0.08) 0%, transparent 70%)',
                borderRadius: '50%',
                pointerEvents: 'none',
                zIndex: 0,
            }} />

            <div style={{ marginBottom: '32px', position: 'relative', zIndex: 10 }}>
                <Link to="/" style={{ textDecoration: 'none' }}>
                    <Logo size="large" dark={false} />
                </Link>
            </div>

            <style>{`
                .auth-form-card {
                    width: 100%;
                    max-width: 420px;
                    padding: 40px;
                    background: rgba(255, 255, 255, 0.03);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 24px;
                    backdrop-filter: blur(20px);
                    position: relative;
                    z-index: 10;
                    box-sizing: border-box;
                }
                @media (max-width: 480px) {
                    .auth-form-card {
                        padding: 24px 20px;
                    }
                }
            `}</style>
            <div className="auth-form-card">
                <h2 style={{ margin: '0 0 8px', textAlign: 'center', fontSize: '1.75rem', fontWeight: 700 }}>
                    Create Account
                </h2>
                <p style={{ color: 'rgba(255, 255, 255, 0.6)', textAlign: 'center', margin: '0 0 28px', fontSize: '0.95rem' }}>
                    Join the You-Bee community today
                </p>

                <form onSubmit={handleRegister} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                    {/* Legal name — required */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                            Legal full name <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <input
                            type="text"
                            required
                            autoComplete="name"
                            placeholder="First Last"
                            value={legalName}
                            onChange={e => setLegalName(e.target.value)}
                            style={inputStyle}
                        />
                        <div style={{
                            margin: '8px 0 0', padding: '10px 14px',
                            background: 'rgba(255, 193, 7, 0.08)',
                            border: '1px solid rgba(255, 193, 7, 0.25)',
                            borderRadius: '8px',
                            fontSize: '0.78rem', color: 'rgba(255, 255, 255, 0.75)', lineHeight: 1.5,
                        }}>
                            <strong style={{ color: '#ffc107' }}>Important:</strong> Your name must exactly match your government-issued ID. This will be verified before any funds can be withdrawn.
                        </div>
                    </div>

                    {/* Email */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                            Email address <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <input
                            type="email"
                            required
                            autoComplete="email"
                            placeholder="you@example.com"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            style={inputStyle}
                        />
                    </div>

                    {/* Password */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                            Password{' '}
                            <span style={{ color: passwordOk ? 'rgba(255, 255, 255, 0.5)' : '#ef4444', fontWeight: 500 }}>
                                (min. 8 characters)
                            </span>
                        </label>
                        <input
                            type="password"
                            required
                            autoComplete="new-password"
                            placeholder="••••••••"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            style={{ ...inputStyle, borderColor: !passwordOk ? '#ef4444' : 'rgba(255, 255, 255, 0.1)' }}
                        />
                    </div>

                    {/* Confirm password */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                            Confirm password
                        </label>
                        <input
                            type="password"
                            required
                            autoComplete="new-password"
                            placeholder="••••••••"
                            value={confirm}
                            onChange={e => setConfirm(e.target.value)}
                            style={{ ...inputStyle, borderColor: !confirmOk ? '#ef4444' : 'rgba(255, 255, 255, 0.1)' }}
                        />
                        {!confirmOk && (
                            <p style={{ margin: '6px 0 0', fontSize: '0.8rem', color: '#ef4444' }}>
                                Passwords do not match
                            </p>
                        )}
                    </div>

                    {error && (
                        <p style={{ margin: 0, color: '#ef4444', fontSize: '0.88rem', fontWeight: 500 }}>
                            {error}
                        </p>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            padding: '14px',
                            background: 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
                            color: '#000',
                            border: 'none',
                            borderRadius: '12px',
                            fontWeight: 700,
                            fontSize: '1rem',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.7 : 1,
                            marginTop: '4px',
                            boxShadow: '0 4px 20px rgba(255, 193, 7, 0.3)',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                        }}
                    >
                        {loading ? 'Creating account…' : 'Create Account'}
                    </button>
                </form>

                {/* Divider */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '24px 0' }}>
                    <div style={{ flex: 1, height: '1px', background: 'rgba(255, 255, 255, 0.1)' }} />
                    <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.82rem' }}>or</span>
                    <div style={{ flex: 1, height: '1px', background: 'rgba(255, 255, 255, 0.1)' }} />
                </div>

                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={() => setError('Google sign-up failed. Please try again.')}
                        text="signup_with"
                        theme="filled_black"
                    />
                </div>

                <p style={{ textAlign: 'center', marginTop: '24px', marginBottom: 0, fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                    Already have an account?{' '}
                    <Link to="/login" style={{ color: '#ffc107', fontWeight: 600, textDecoration: 'none' }}>
                        Sign in
                    </Link>
                </p>
            </div>
        </div>
    );
}

export default Register;
